// Utilities for the Bank account history page.

import type { BankTransaction, BankHistoryFilter, DatePreset } from "./types";

export function fmtDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}

export function fmtAmount(amount: number | undefined): string {
  if (amount == null) return "";
  return amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtCurrency(amount: number | undefined | null): string {
  if (amount == null) return "—";
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  return sign + "$" + abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function labelForType(type: BankTransaction["type"]): string {
  const map: Record<BankTransaction["type"], string> = {
    "payment": "Payment",
    "cheque": "Cheque",
    "deposit": "Deposit",
    "transfer": "Transfer",
    "debit": "Debit",
    "payroll": "Payroll",
    "interest": "Interest",
    "bank-fee": "Bank fee",
  };
  return map[type];
}

export function dateRangeForPreset(preset: DatePreset, now: Date = new Date()): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, "0");
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (preset) {
    case "this-month":
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    case "last-month":
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case "this-quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return { from: iso(new Date(y, qStart, 1)), to: iso(new Date(y, qStart + 3, 0)) };
    }
    case "this-year":
      return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) };
    default:
      return { from: "", to: "" };
  }
}

export function applyFilters(txs: BankTransaction[], filter: BankHistoryFilter): BankTransaction[] {
  const findLc = filter.find.trim().toLowerCase();
  const effectiveRange = filter.datePreset === "custom"
    ? { from: filter.fromDate, to: filter.toDate }
    : dateRangeForPreset(filter.datePreset);

  return txs.filter((t) => {
    // Find: payee, memo, refNo, amount, >$amt, <$amt
    if (findLc) {
      let matched = false;
      if (t.payee.toLowerCase().includes(findLc)) matched = true;
      if (!matched && t.memo && t.memo.toLowerCase().includes(findLc)) matched = true;
      if (!matched && t.refNo.toLowerCase().includes(findLc)) matched = true;

      // Amount comparisons
      const amt = t.payment ?? t.deposit ?? 0;
      if (!matched) {
        const gt = findLc.match(/^>\$?(\d+(\.\d+)?)/);
        const lt = findLc.match(/^<\$?(\d+(\.\d+)?)/);
        if (gt && amt > parseFloat(gt[1])) matched = true;
        else if (lt && amt < parseFloat(lt[1])) matched = true;
        else if (!gt && !lt) {
          const plain = findLc.replace(/[\$,]/g, "");
          if (plain && String(amt).includes(plain)) matched = true;
        }
      }
      if (!matched) return false;
    }

    if (filter.reconcileStatus !== "all" && t.reconcileStatus !== filter.reconcileStatus) return false;
    if (filter.transactionType !== "all" && t.type !== filter.transactionType) return false;
    if (filter.payee !== "all" && t.payee !== filter.payee) return false;

    if (effectiveRange.from && t.date < effectiveRange.from) return false;
    if (effectiveRange.to && t.date > effectiveRange.to) return false;

    return true;
  });
}

/**
 * Compute running balance per row, newest-first.
 * The newest row's balance = account's current balance; each row below
 * reverses the effect of the transaction above it.
 */
export function computeRunningBalances(txs: BankTransaction[], endingBalance: number): Array<BankTransaction & { runningBalance: number }> {
  // Newest first
  const sorted = [...txs].sort((a, b) => (a.date < b.date ? 1 : -1));
  let bal = endingBalance;
  const out: Array<BankTransaction & { runningBalance: number }> = [];
  for (const t of sorted) {
    out.push({ ...t, runningBalance: bal });
    const delta = (t.deposit ?? 0) - (t.payment ?? 0);
    bal = bal - delta;
  }
  return out;
}

export function getUniquePayees(txs: BankTransaction[]): string[] {
  const set = new Set<string>();
  for (const t of txs) set.add(t.payee);
  return Array.from(set).sort();
}